/**
 * Admin architecture page: two static diagrams, how the platform is wired today
 * and the target. A thinking aid; no API call.
 */

import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  Position,
  type Edge,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { PageHeader } from "../components/layout/PageHeader";
import { useProfile } from "../contexts/ProfileContext";
import {
  TARGET,
  TODAY,
  type ArchitectureDiagram,
  type ArchitectureLayer,
} from "../lib/architectureDiagram";

const LAYER_COLORS: Record<ArchitectureLayer, string> = {
  clients: "bg-blue-500/15 border-blue-500/40 text-blue-400",
  entry: "bg-orange-500/15 border-orange-500/40 text-orange-400",
  hub: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400",
  machine: "bg-purple-500/15 border-purple-500/40 text-purple-400",
  signin: "bg-slate-500/15 border-slate-500/40 text-slate-400",
  data: "bg-slate-500/15 border-slate-500/40 text-slate-400",
};

// Same colour in both diagrams: the nodes that decide who may do what.
const DECISION_CLASSES = "bg-rose-500/15 border-rose-500/60 ring-2 ring-rose-500/40";

type ArchNodeData = {
  layer: ArchitectureLayer;
  decision: boolean;
  layerLabel: string;
  label: string;
  description: string;
  [key: string]: unknown;
};

function ArchNode({ data }: NodeProps<Node<ArchNodeData>>) {
  const colors = data.decision ? DECISION_CLASSES : LAYER_COLORS[data.layer].split(" ").slice(0, 2).join(" ");
  const accent = LAYER_COLORS[data.layer].split(" ")[2];
  return (
    <div className={`rounded-xl border ${colors} px-4 py-3 w-[210px] shadow-lg`}>
      <Handle type="target" position={Position.Top} className="!bg-muted-foreground/50 !w-2 !h-2 !border-0" />
      <div className={`text-[10px] uppercase tracking-wide mb-0.5 ${accent}`}>{data.layerLabel}</div>
      <div className="text-sm font-semibold text-foreground leading-tight break-words">{data.label}</div>
      <div className="text-[11px] text-muted-foreground leading-snug mt-1">{data.description}</div>
      <Handle type="source" position={Position.Bottom} className="!bg-muted-foreground/50 !w-2 !h-2 !border-0" />
    </div>
  );
}

const nodeTypes = { arch: ArchNode };

function Diagram({ id, diagram }: { id: "today" | "target"; diagram: ArchitectureDiagram }) {
  const { t } = useTranslation();
  const nodes: Node[] = diagram.nodes.map((n) => ({
    id: n.id,
    type: "arch",
    position: { x: n.x, y: n.y },
    data: {
      layer: n.layer,
      decision: !!n.decision,
      layerLabel: t(`architecture.layers.${n.layer}`),
      label: t(`architecture.${id}.nodes.${n.id}.label`),
      description: t(`architecture.${id}.nodes.${n.id}.description`),
    } satisfies ArchNodeData,
  }));
  const edges: Edge[] = diagram.edges.map(([from, to]) => ({
    id: `${from}-${to}`,
    source: from,
    target: to,
    style: { strokeWidth: 1.5 },
  }));

  return (
    <div className="h-[640px] rounded-xl border border-border bg-card/40">
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.1 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnScroll
          minZoom={0.3}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}

function Section({ id, diagram, points, notes }: { id: "today" | "target"; diagram: ArchitectureDiagram; points: string[]; notes: string[] }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby={`architecture-${id}`} className="min-w-0 space-y-3">
      <div>
        <h2 id={`architecture-${id}`} className="text-lg font-semibold text-foreground">
          {t(`architecture.${id}.title`)}
        </h2>
        <p className="text-sm text-muted-foreground">{t(`architecture.${id}.subtitle`)}</p>
      </div>
      <Diagram id={id} diagram={diagram} />
      <ul className="list-disc pl-5 text-sm text-foreground space-y-1">
        {points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      {notes.map((n) => (
        <p key={n} className="text-xs text-muted-foreground">{n}</p>
      ))}
    </section>
  );
}

export function AdminArchitecture() {
  const { isAdmin } = useProfile();
  const { t } = useTranslation();

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <>
      <PageHeader title={t("admin.architecture")} />
      <div className="p-4 space-y-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className={`inline-block h-3 w-3 rounded border ${DECISION_CLASSES}`} />
          {t("architecture.legend.decision")}
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Section
            id="today"
            diagram={TODAY}
            points={[t("architecture.today.points.hubDependency"), t("architecture.today.points.twoDeciders")]}
            notes={[t("architecture.today.notes.trust")]}
          />
          <Section
            id="target"
            diagram={TARGET}
            points={[t("architecture.target.points.sideBySide"), t("architecture.target.points.oneDecider")]}
            notes={[t("architecture.target.notes.loadBalancer"), t("architecture.target.notes.status")]}
          />
        </div>
      </div>
    </>
  );
}
